import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { createCategory, CreateCategoryData, updateCategory, UpdateCategoryData, getCategory } from '../../shared/api/categories';
import { useAuth } from '../../shared/auth/AuthContext';
import { getAdminLoginPath, getAdminCategoriesPath } from '../../shared/config/env';
import AdminSidebar from '../components/AdminSidebar';
import './CategoryCreate.css';

function CategoryCreate() {
  const { id: categoryId } = useParams<{ id?: string }>();
  const isEditMode = categoryId !== undefined;
  
  const [name, setName] = useState('');
  const [nameEn, setNameEn] = useState('');
  const [slug, setSlug] = useState('');
  const [isVisible, setIsVisible] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  
  const { logout } = useAuth();
  const navigate = useNavigate();
  const adminLoginPath = getAdminLoginPath();
  const adminCategoriesPath = getAdminCategoriesPath();

  // 編集モード時、既存カテゴリーデータを読み込む
  useEffect(() => {
    if (isEditMode && categoryId) {
      const fetchCategory = async () => {
        try {
          setIsLoading(true);
          setError(null);
          const response = await getCategory(categoryId);
          const category = response.category;
          setName(category.name);
          setNameEn(category.nameEn || '');
          setSlug(category.slug);
          setIsVisible(category.isVisible);
        } catch (err) {
          if (err instanceof Error && (err as any).isAuthError) {
            logout();
            navigate(adminLoginPath, { replace: true });
            return;
          }
          if (err instanceof Error) {
            setError(err.message);
          } else {
            setError('カテゴリーの取得に失敗しました');
          }
        } finally {
          setIsLoading(false);
        }
      };
      
      fetchCategory();
    }
  }, [isEditMode, categoryId, logout, navigate, adminLoginPath]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // バリデーション
    if (!name.trim()) {
      setError('名前（日本語）は必須です');
      return;
    }

    setIsSubmitting(true);

    try {
      if (isEditMode) {
        // 編集モード
        if (!categoryId) {
          setError('カテゴリーIDが取得できませんでした');
          setIsSubmitting(false);
          return;
        }
        
        const categoryData: UpdateCategoryData = {
          name: name.trim(),
          ...(nameEn.trim() && { name_en: nameEn.trim() }),
          ...(slug.trim() && { slug: slug.trim() }),
          is_visible: isVisible,
        };

        await updateCategory(categoryId, categoryData);
        navigate(adminCategoriesPath);
      } else {
        // 作成モード
        const categoryData: CreateCategoryData = {
          name: name.trim(),
          ...(nameEn.trim() && { name_en: nameEn.trim() }),
          ...(slug.trim() && { slug: slug.trim() }),
          is_visible: isVisible,
        };

        await createCategory(categoryData);
        navigate(adminCategoriesPath);
      }
    } catch (err) {
      if (err instanceof Error && (err as any).isAuthError) {
        logout();
        navigate(adminLoginPath, { replace: true });
        return;
      }
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError(isEditMode ? 'カテゴリーの更新に失敗しました' : 'カテゴリーの作成に失敗しました');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate(adminCategoriesPath);
  };

  return (
    <div className="admin-layout">
      <AdminSidebar />
      <div className="admin-layout__main">
        <div className="category-create">
          <header className="category-create__header">
            <h1 className="category-create__title">
              {isEditMode ? 'カテゴリー編集' : 'カテゴリー新規作成'}
            </h1>
            <div className="category-create__actions">
              <button
                type="button"
                onClick={handleCancel}
                className="category-create__button category-create__button--secondary"
                disabled={isSubmitting || isLoading}
              >
                キャンセル
              </button>
              <button
                type="submit"
                form="category-form"
                className="category-create__button category-create__button--primary"
                disabled={isSubmitting || isLoading}
              >
                {isSubmitting ? '保存中...' : '保存'}
              </button>
            </div>
          </header>
          <main className="category-create__content">
            {isLoading ? (
              <p className="category-create__loading">読み込み中...</p>
            ) : (
              <>
                {error && (
                  <div className="category-create__error">
                    {error}
                  </div>
                )}
                <form id="category-form" onSubmit={handleSubmit} className="category-create__form">
                  <div className="category-create__form-group">
                    <label htmlFor="name" className="category-create__label">
                      名前（日本語） <span className="category-create__required">*</span>
                    </label>
                    <input
                      type="text"
                      id="name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="例: 技術"
                      disabled={isSubmitting || isLoading}
                      className="category-create__input"
                      required
                    />
                  </div>

                  <div className="category-create__form-group">
                    <label htmlFor="nameEn" className="category-create__label">
                      名前（英語）
                    </label>
                    <input
                      type="text"
                      id="nameEn"
                      value={nameEn}
                      onChange={(e) => setNameEn(e.target.value)}
                      placeholder="例: Technology"
                      disabled={isSubmitting || isLoading}
                      className="category-create__input"
                    />
                    <small className="category-create__help">
                      slugが未指定の場合、この値から自動生成されます
                    </small>
                  </div>

                  <div className="category-create__form-group">
                    <label htmlFor="slug" className="category-create__label">
                      スラッグ
                    </label>
                    <input
                      type="text"
                      id="slug"
                      value={slug}
                      onChange={(e) => setSlug(e.target.value)}
                      placeholder="例: technology"
                      disabled={isSubmitting || isLoading}
                      className="category-create__input"
                    />
                    <small className="category-create__help">
                      URLで使用されるスラッグ（未指定の場合は英語名から自動生成）
                    </small>
                  </div>

                  <div className="category-create__form-group">
                    <label htmlFor="isVisible" className="category-create__label">
                      <input
                        type="checkbox"
                        id="isVisible"
                        checked={isVisible}
                        onChange={(e) => setIsVisible(e.target.checked)}
                        disabled={isSubmitting || isLoading}
                        className="category-create__checkbox"
                      />
                      表示する
                    </label>
                    <small className="category-create__help">
                      チェックを外すと、Home画面のカテゴリー一覧に表示されません
                    </small>
                  </div>
                </form>
              </>
            )}
          </main>
        </div>
      </div>
    </div>
  );
}

export default CategoryCreate;

